import { defineStore } from "pinia";
import {
    WorkLog,
    WorkLogEdit,
    WorkLogId,
    WorkLogTicketAllocation,
    workLogApi,
} from "@/api/workLogs";
import type { OrganizationId } from "@/api/organizations";
import type { ProjectId } from "@/api/projects";

export type WorkLogsState = {
    entries: WorkLog[] | undefined;
    loading: boolean;
    /**
     * Server clock minus browser clock. Elapsed time is measured against this so
     * a machine with a skewed clock does not show a timer running backwards.
     */
    clockOffsetMs: number;
    /** The window currently on screen, so a refetch does not silently change it. */
    range: { from: Date | undefined; to: Date | undefined };
    /** Mirrors the site rule so the forms refuse an untargeted entry before the server does. */
    requireTarget: boolean;
};

function newestFirst(entries: WorkLog[]): WorkLog[] {
    return [...entries].sort((a, b) => b.started.getTime() - a.started.getTime());
}

export const useWorkLogsStore = defineStore("workLogs", {
    state: (): WorkLogsState => ({
        entries: undefined,
        loading: false,
        clockOffsetMs: 0,
        range: { from: undefined, to: undefined },
        requireTarget: false,
    }),
    getters: {
        running(state): WorkLog[] {
            return (state.entries ?? []).filter((e) => e.stopped === null);
        },
        stopped(state): WorkLog[] {
            return (state.entries ?? []).filter((e) => e.stopped !== null);
        },
    },
    actions: {
        async load(force = false) {
            if (this.$state.entries !== undefined && !force) {
                // The cached window renders instantly; revalidate it in the
                // background so entries added elsewhere still show up.
                const { from, to } = this.$state.range;
                void this.refreshRange(from, to).catch(() => undefined);
                return;
            }

            await this.loadRange();
        },
        // Another tab, device, or the MCP server changed the user's entries; refetch the
        // window on screen in place. Nothing loaded yet means nothing to go stale.
        async revalidate() {
            if (this.$state.entries === undefined) return;
            const { from, to } = this.$state.range;
            await this.refreshRange(from, to).catch(() => undefined);
        },
        async loadRange(from?: Date, to?: Date) {
            this.$state.loading = true;
            try {
                await this.refreshRange(from, to);
            } finally {
                this.$state.loading = false;
            }
        },
        // Fetches without flipping `loading`, so an in-place refresh never
        // blanks the list to a spinner under the user.
        async refreshRange(from?: Date, to?: Date) {
            this.$state.range = { from, to };
            const requestedAt = Date.now();
            const result = await workLogApi.getWorkLogs(from, to);
            this.$state.clockOffsetMs = result.serverNow.getTime() - requestedAt;
            this.$state.entries = newestFirst(result.entries);
            this.$state.requireTarget = result.requireTarget;
        },
        async start(
            text: string | null = null,
            tickets: WorkLogTicketAllocation[] = [],
            projectId: ProjectId | null = null,
            organizationId: OrganizationId | null = null
        ) {
            const entry = await workLogApi.start(text, tickets, projectId, organizationId);
            this.$state.entries = newestFirst([...(this.$state.entries ?? []), entry]);

            return entry;
        },
        async stop(id: WorkLogId) {
            this.replace(await workLogApi.stop(id));
        },
        async stopAll() {
            const stopped = await workLogApi.stopAll();
            // Stopping the lot rewrites several entries at once, so the list is
            // refetched rather than patched entry by entry, over whatever window
            // is currently on screen.
            if (stopped > 0) {
                const { from, to } = this.$state.range;
                await this.loadRange(from, to);
            }

            return stopped;
        },
        async log(
            text: string | null,
            started: Date,
            stopped: Date,
            tickets: WorkLogTicketAllocation[] = [],
            projectId: ProjectId | null = null,
            organizationId: OrganizationId | null = null
        ) {
            const entry = await workLogApi.log(
                text,
                started,
                stopped,
                tickets,
                projectId,
                organizationId
            );
            this.$state.entries = newestFirst([...(this.$state.entries ?? []), entry]);

            return entry;
        },
        async update(id: WorkLogId, edit: WorkLogEdit) {
            const updated = await workLogApi.update(id, edit);
            // Time Review edits other people's entries through here too; those
            // must not land in the list of my own.
            if ((this.$state.entries ?? []).some((e) => e.id === id)) {
                this.replace(updated);
                this.$state.entries = newestFirst(this.$state.entries ?? []);
            }

            return updated;
        },
        async remove(id: WorkLogId) {
            await workLogApi.remove(id);
            this.$state.entries = (this.$state.entries ?? []).filter((e) => e.id !== id);
        },
        replace(entry: WorkLog) {
            const entries = this.$state.entries ?? [];
            const index = entries.findIndex((e) => e.id === entry.id);

            if (index >= 0) {
                entries.splice(index, 1, entry);
            } else {
                this.$state.entries = newestFirst([...entries, entry]);
            }
        },
    },
});
